import { FormControlLabel, FormGroup, Switch } from '@mui/material';
import * as React from 'react';

import InputValidator from '@/components/validators-forms/input-validator';
import WrapRequiredFieldsDescriptionComponent from '@/views/common-components/wrap-required-fileds-description';
import { SensorSettingsState } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPanelComponent, TabPropsType } from '.';

const isShown = (state: SensorSettingsState): boolean => {
  return (
    state.peripheralType === 'SENSOR' &&
    !!state.config?.sensor?.type &&
    (state.config?.sensor.type.startsWith('COUNTER') || state.config?.sensor.type === 'DISCRETE')
  );
};

export const TabProps: TabPropsType = {
  isShown,
  isValid: (state) => {
    const sensor = state.config?.sensor;
    return (
      sensor?.stopValueThreshold !== undefined &&
      sensor?.stopValueThreshold !== null &&
      sensor?.stopThreshold !== null &&
      sensor?.stopThreshold !== undefined &&
      sensor?.stopRegisterThreshold !== null &&
      sensor?.stopRegisterThreshold !== undefined &&
      sensor?.stopThreshold >= (1).seconds &&
      sensor?.stopThreshold <= sensor?.stopRegisterThreshold &&
      sensor?.stopValueThreshold >= 0
    );
  },
  label: (t) => t('device:stopSetup', { defaultValue: 'Stops' }),
};

export const TabPanel: TabPanelComponent = ({ handleChange, state, classes, t, invalidFormMessage }) => {
  if (!isShown(state)) {
    return null;
  }

  const onStopThresholdChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        // Convert from seconds to milliseconds
        stopThreshold: val.trim().length ? parseInt(val, 10) * 1000 : undefined,
      },
    });
  };

  const onStopValueThresholdChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        stopValueThreshold: val.trim().length ? parseFloat(val) : undefined,
      },
    });
  };

  const onRegisterThresholdChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        // Convert from seconds to milliseconds
        stopRegisterThreshold: val.trim().length ? parseInt(val, 10) * 1000 : undefined,
      },
    });
  };

  const changeFindStops = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    handleChange({
      sensor: {
        findStops: checked,
      },
    });
  };

  const stopThresholdAsSecs = state?.config?.sensor?.stopThreshold
    ? Math.floor(state.config.sensor.stopThreshold / 1000)
    : '';
  const stopRegisterThresholdAsSecs = state?.config?.sensor?.stopRegisterThreshold
    ? Math.floor(state?.config?.sensor?.stopRegisterThreshold / 1000)
    : '';

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        disabled={!state?.config?.sensor?.findStops}
        type="number"
        name="stopThreshold"
        label={t(['device:minimumStopDurationInSeconds'], {
          defaultValue: 'Minimum stop duration in seconds',
        })}
        validators={['required', 'isPositive', 'minNumber:1']}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:cannotBeLowerThanOne'], {
            defaultValue: 'the threshold cannot be lower than 1 second',
          }),
        ]}
        value={stopThresholdAsSecs}
        helperText={
          <span>
            {t(['device:configurationStopThresholdExplanation'], {
              defaultValue:
                'All future stops will need to be at least {{stopThreshold}} seconds long before it is defined as a stop.',
              stopThreshold: stopThresholdAsSecs,
            })}
            {t(['device:configurationMinimumStopLengthExplanation'], {
              defaultValue: 'The minimum length of a stop is defined by the version of your Factbird',
            })}
            .
          </span>
        }
        onChange={onStopThresholdChange}
      />
      <br />
      <br />
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        disabled={!state?.config?.sensor?.findStops}
        type="number"
        name="stopValueThreshold"
        label={t(['device:stopValueThreshold'], {
          defaultValue: 'Value threshold for when the sensor is thought of as stopped',
        })}
        validators={['required', 'isPositive']}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
        ]}
        helperText={
          <span>
            {t(['device:configurationStopValueThresholdExplanation'], {
              defaultValue: 'Producing less than {{stopValueThreshold}} pcs per minute will be considered a stop.',
              stopValueThreshold: state?.config?.sensor?.stopValueThreshold ?? 0,
            })}
          </span>
        }
        value={state?.config?.sensor?.stopValueThreshold}
        onChange={onStopValueThresholdChange}
      />
      <br />
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        disabled={!state?.config?.sensor?.findStops}
        type="number"
        name="stopRegisterThreshold"
        label={t(['device:stopRegistrationThreshold'], {
          defaultValue: 'Stop registration threshold in seconds',
        })}
        validators={['required', 'isPositive', `minNumber:${stopThresholdAsSecs}`]}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:cannotBeLowerThanMinStopDuration'], {
            seconds: stopThresholdAsSecs,
            defaultValue: 'the threshold cannot be lower than the stop duration, which is {{ seconds }} seconds',
          }),
        ]}
        // Convert from millis to seconds
        value={stopRegisterThresholdAsSecs}
        helperText={
          <span>
            {t(['device:configurationStopRegisterThresholdExplanation'], {
              defaultValue:
                'You will only be prompted to register stops that are {{stopRegisterThreshold}} seconds or longer. Everything between {{stopThreshold}}s and {{stopRegisterThreshold}}s will be considered micro stops.',
              stopRegisterThreshold: stopRegisterThresholdAsSecs,
              stopThreshold: stopThresholdAsSecs,
            })}
          </span>
        }
        onChange={onRegisterThresholdChange}
      />
      <FormGroup style={{ marginTop: '12px' }}>
        <FormControlLabel
          control={<Switch color="primary" onChange={changeFindStops} checked={!!state?.config?.sensor?.findStops} />}
          label={t(['shared:enableFindStops'], { defaultValue: 'Enable finding stops' })}
        />
      </FormGroup>
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};
